/**
 * TopBar — redesigned header.
 *
 * Layout (left -> right):
 *   [Page title + breadcrumb]   [Search]   [Date range pill]   [Notifications]   [Settings]   [Avatar pill]
 */
import React, { useState, useEffect, useRef } from 'react';
import { tokensV2 as T } from './tokens.v2';
import { useLiveFeed } from './useLiveFeed.v2';

const RANGE_OPTIONS = [
  { id: 'today',      label: 'Today' },
  { id: 'this-week',  label: 'This week' },
  { id: 'this-month', label: 'This month' },
  { id: 'last-month', label: 'Last month' },
  { id: 'this-year',  label: 'This year' },
  { id: 'last-year',  label: 'Last year' },
  { id: 'till-date',  label: 'Till date' },
  { id: 'custom',     label: 'Custom range' },
];

function timeAgo(then) {
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 5)  return 'just now';
  if (sec < 60) return sec + ' sec ago';
  const min = Math.floor(sec / 60);
  if (min < 60) return min + ' min ago';
  const hr = Math.floor(min / 60);
  if (hr < 24)  return hr + ' hr ago';
  return Math.floor(hr / 24) + ' day ago';
}

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside]);
}

function IconButton({ children, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      style={{
        width: 38, height: 38, borderRadius: 12,
        backgroundColor: T.surface.card,
        border: '1px solid ' + T.border.subtle,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', outline: 'none', flexShrink: 0,
        color: T.text.secondary,
      }}
    >
      {children}
    </button>
  );
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="7" x2="20" y2="7"/>
      <line x1="4" y1="12" x2="20" y2="12"/>
      <line x1="4" y1="17" x2="20" y2="17"/>
    </svg>
  );
}

function PulseDot() {
  return (
    <span style={{ position: "relative", display: "inline-flex", width: 8, height: 8 }}>
      <span style={{ position: "absolute", inset: 0, backgroundColor: T.status.good, borderRadius: "50%", opacity: 0.6, animation: "fc-pulse-hdr 1.6s cubic-bezier(0,0,0.2,1) infinite" }} />
      <span style={{ position: "relative", width: 8, height: 8, borderRadius: "50%", backgroundColor: T.status.good }} />
      <style>{"@keyframes fc-pulse-hdr { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.6); opacity: 0; } }"}</style>
    </span>
  );
}

/**
 * LiveTicker — rotating ticker of recent platform events.
 *
 * Visual language aligned to the dashboard's Jobie-inspired palette:
 *   • Cream/white pill, subtle border, soft shadow (matches every Tile
 *     and Section card)
 *   • Brand orange is the ONLY saturated accent (the LIVE pulse + the
 *     subject name). Everything else is muted.
 *   • Per-kind icons sit in a tiny tinted disc with the SAME hue family
 *     used by the dashboard's "muted-*" tile variants — pastels at very
 *     low saturation so they read as quiet category tags, not stickers.
 *   • Subject (name) bolded inline; action (verb) in muted secondary text.
 */
/**
 * Per-kind theme — the disc background tells you WHAT kind of event,
 * the initial inside the disc tells you WHO did it (subject's first
 * letter). The kindLabel is shown as a tiny tag below the disc on hover
 * for screen readers + the title attribute, so the color isn't the only
 * carrier of meaning.
 */
const LIVE_KIND_THEME = {
  // bg = soft tint at ~12% alpha (matches the dashboard's "muted-*" tile palette)
  // fg = the matching darker hue used for the initial letter
  fan_joined:     { bg: 'rgba(252,128,25,0.14)',  fg: '#C5530A', label: 'New fan' },         // muted-orange
  creator_joined: { bg: 'rgba(46,180,176,0.14)',  fg: '#0F7A75', label: 'New creator' },     // muted-teal
  fan_recharged:  { bg: 'rgba(76,175,80,0.14)',   fg: '#0E7B47', label: 'Recharge' },        // muted-lime
  call_completed: { bg: 'rgba(124,77,255,0.14)',  fg: '#4A2EAA', label: 'Call completed' },  // muted-violet
  brand_campaign: { bg: 'rgba(255,107,157,0.14)', fg: '#A8366B', label: 'Brand campaign' },  // muted-magenta
  creator_payout: { bg: 'rgba(11,91,184,0.12)',   fg: '#0B5BB8', label: 'Payout' },          // muted-blue
};
const LIVE_KIND_DEFAULT = { bg: T.brand.soft, fg: T.brand.deep, label: 'Live event' };

/** First letter of the first word, uppercased. Falls back to '•' for empty. */
function subjectInitial(subject) {
  const s = String(subject || '').trim();
  if (!s) return '•';
  // First non-whitespace, non-emoji-ish character. Strip leading symbols.
  const cleaned = s.replace(/^[^A-Za-z0-9]+/, '');
  return (cleaned[0] || s[0] || '•').toUpperCase();
}

/** Split a backend-formatted message into [subject, action] so we can
 *  render the subject in primary text weight and the verb in muted. */
function splitLiveMessage(msg) {
  if (!msg) return { subject: '', action: '' };
  // Each backend message starts with the subject (name or brand) followed
  // by a verb phrase. Heuristic: first 1–3 words before the first verb is
  // the subject. We split on the first space — the AI-formatted payload
  // is consistent enough that "<Subject> <verb-phrase>" works.
  const i = msg.indexOf(' ');
  if (i <= 0) return { subject: msg, action: '' };
  return { subject: msg.slice(0, i), action: msg.slice(i + 1) };
}

function LiveTicker({ fallbackAgo }) {
  const { currentEvent, status } = useLiveFeed();
  const theme = currentEvent ? (LIVE_KIND_THEME[currentEvent.kind] || LIVE_KIND_DEFAULT) : LIVE_KIND_DEFAULT;
  const { subject, action } = splitLiveMessage(currentEvent?.message);
  const ts = currentEvent?.createdAt
    ? new Date(currentEvent.createdAt).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })
    : null;
  // Choose what to show in the message slot when there's no current event.
  // We deliberately never surface a "paused / error" string to the CEO —
  // any fetch failure (network blip, 5xx, 401, older deploy w/o /live/feed)
  // degrades quietly to the same "Synced X ago" copy. The technical detail
  // stays in the console where engineers can find it.
  const idleMessage = status === "loading"
    ? "Fetching live activity…"
    : `Synced ${fallbackAgo}`;

  const initial = subjectInitial(subject);
  const tooltip = ts && currentEvent
    ? `${theme.label} · ${ts}`
    : `Live · synced ${fallbackAgo}`;
  return (
    <div
      title={tooltip}
      className="fc-live-ticker"
      style={{
        display: 'flex', alignItems: 'center', gap: 0,
        height: 38, flexShrink: 1, minWidth: 0, maxWidth: 480,
        background: T.surface.card,
        border: `1px solid ${T.border.subtle}`,
        borderRadius: 999,
        boxShadow: '0 1px 2px rgba(31,42,63,0.04), 0 6px 18px rgba(252,128,25,0.05)',
        fontFamily: T.font.sans,
        position: 'relative', overflow: 'hidden',
      }}>
      {/* Brand-orange shimmer sweep — runs every 8s, very subtle, gives the
          pill a sense of "alive". Translates a faint orange highlight from
          left to right across the surface, masked by overflow:hidden. */}
      <div aria-hidden style={{
        position: 'absolute', top: 0, left: 0, height: '100%', width: '40%',
        pointerEvents: 'none',
        background: `linear-gradient(90deg, transparent 0%, rgba(252,128,25,0.08) 50%, transparent 100%)`,
        animation: 'fc-ticker-shimmer 8s ease-in-out infinite',
      }} />

      {/* LEFT — pulsing live dot, label */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '0 12px 0 14px',
        flexShrink: 0, height: '100%',
        position: 'relative', zIndex: 1,
      }}>
        <PulseDot />
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.10em',
          textTransform: 'uppercase', color: T.text.muted,
        }}>
          Live
        </span>
      </div>

      <span style={{ width: 1, height: 18, background: T.border.subtle, flexShrink: 0, zIndex: 1 }} />

      {/* MIDDLE — subject-initial avatar (Slack/Linear notification style).
          The disc bg encodes the kind (orange = fan, teal = creator,
          lime = recharge, violet = call, magenta = brand, blue = payout)
          and the LETTER inside is the first character of the subject's
          name. So you see "A" in an orange circle = "Aarav, new fan".
          The halo behind the disc gives a soft heartbeat glow without
          adding emoji clutter. */}
      {currentEvent && (
        <div style={{
          position: 'relative', margin: '0 10px 0 12px',
          width: 26, height: 26, flexShrink: 0, zIndex: 1,
        }}>
          <div aria-hidden style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: theme.bg,
            animation: 'fc-ticker-halo 1.8s ease-out infinite',
          }} />
          <div
            key={`badge-${currentEvent.id}`}
            style={{
              position: 'relative',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 26, height: 26, borderRadius: '50%',
              background: theme.bg, color: theme.fg,
              fontSize: 11, fontWeight: 800, letterSpacing: '0.01em',
              fontFamily: T.font.sans, lineHeight: 1,
              animation: 'fc-ticker-pop 700ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              transformOrigin: 'center',
              userSelect: 'none',
            }}>
            {initial}
          </div>
        </div>
      )}

      {/* RIGHT — message slides in from the right like a stock ticker. */}
      <div style={{
        flex: 1, minWidth: 0, height: '100%',
        display: 'flex', alignItems: 'center',
        padding: currentEvent ? '0 16px 0 0' : '0 16px',
        overflow: 'hidden',
        position: 'relative', zIndex: 1,
      }}>
        <span
          key={currentEvent?.id || 'idle'}
          style={{
            fontSize: 13,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            minWidth: 0, lineHeight: 1.2,
            animation: 'fc-ticker-marquee 700ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
          {currentEvent
            ? <>
                <span style={{ fontWeight: 700, color: T.text.primary }}>{subject}</span>
                {action ? <span style={{ fontWeight: 500, color: T.text.secondary }}> {action}</span> : null}
              </>
            : <span style={{ fontWeight: 500, color: T.text.muted }}>
                {idleMessage}
              </span>}
        </span>
      </div>

      <style>{`
        @keyframes fc-ticker-marquee {
          0%   { opacity: 0; transform: translateX(28px); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes fc-ticker-pop {
          0%   { transform: scale(0.6); opacity: 0; }
          70%  { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes fc-ticker-halo {
          0%   { transform: scale(1); opacity: 0.55; }
          80%  { transform: scale(1.85); opacity: 0; }
          100% { transform: scale(1.85); opacity: 0; }
        }
        @keyframes fc-ticker-shimmer {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(280%); }
          100% { transform: translateX(280%); }
        }
        .fc-live-ticker {
          transition:
            transform 320ms cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 320ms cubic-bezier(0.16, 1, 0.3, 1),
            border-color 280ms ease;
        }
        .fc-live-ticker:hover {
          transform: translateY(-1px);
          border-color: rgba(252,128,25,0.45);
          box-shadow:
            0 0 0 3px rgba(252,128,25,0.06),
            0 12px 24px -8px rgba(252,128,25,0.18),
            0 24px 48px -16px rgba(252,128,25,0.12),
            inset 0 0 0 1px rgba(252,128,25,0.10);
        }
        /* Make the brand shimmer step more visibly during hover so the
           pill feels properly "awake" while the cursor sits on it. */
        .fc-live-ticker:hover > [aria-hidden] {
          animation-duration: 3.2s;
        }
      `}</style>
    </div>
  );
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

// DD/MM/YY (Indian convention) — same format used on the trend chart.
//   single date  → "01/05/26"
//   range        → "01/05/26 → 14/05/26"
function fmtPillRange(fromStr, toStr) {
  if (!fromStr) return null;
  const ddmmyy = (yyyymmdd) => {
    if (!yyyymmdd || yyyymmdd.length < 10) return null;
    const y = yyyymmdd.slice(2, 4);
    const m = yyyymmdd.slice(5, 7);
    const d = yyyymmdd.slice(8, 10);
    return `${d}/${m}/${y}`;
  };
  const fStr = ddmmyy(fromStr);
  if (!toStr || fromStr === toStr) return fStr;
  return `${fStr} → ${ddmmyy(toStr)}`;
}

function DateRangePill({ rangeId, customRange, onChange, resolvedRange, prevRange }) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(rangeId === 'custom');
  const [from, setFrom] = useState(customRange ? customRange.from : '');
  const [to, setTo]     = useState(customRange ? customRange.to   : todayIso());
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  const current = RANGE_OPTIONS.find(o => o.id === rangeId) || RANGE_OPTIONS[2];

  const labelForCurrent = rangeId === 'custom' && customRange && customRange.from
    ? customRange.from + ' to ' + customRange.to
    : current.label;

  // Resolved dates as a small subline on the pill, e.g.
  //   "May 1 · vs Apr 30"
  //   "May 1 → 14 · vs Apr 1 → 14"
  // Drops the "vs ..." half for till-date / lifetime where no comparison
  // applies. Tells the user EXACTLY what's on screen and what it's against.
  const subline = (() => {
    if (!resolvedRange) return null;
    const cur = fmtPillRange(resolvedRange.from, resolvedRange.to);
    if (!cur) return null;
    if (!prevRange || !prevRange.from || !prevRange.to) return cur;
    const prev = fmtPillRange(prevRange.from, prevRange.to);
    return prev ? `${cur}  ·  vs ${prev}` : cur;
  })();

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px',
        background: T.surface.card, border: '1px solid ' + (open ? T.brand.base : T.border.subtle),
        borderRadius: 12, color: T.text.primary,
        cursor: 'pointer', outline: 'none', fontFamily: T.font.sans, whiteSpace: 'nowrap',
      }}>
        <span style={{ color: T.brand.base, display: 'inline-flex' }}><CalendarIcon /></span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{labelForCurrent}</span>
          {subline && (
            <span style={{ fontSize: 10, fontWeight: 500, color: T.text.muted, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{subline}</span>
          )}
        </span>
        <span style={{ color: T.text.muted, marginLeft: 2, transition: 'transform 120ms ease', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: 220,
          backgroundColor: T.surface.card, border: '1px solid ' + T.border.subtle,
          borderRadius: 12, boxShadow: '0 12px 32px rgba(31,42,63,0.12)',
          padding: 6, zIndex: 20,
        }}>
          {RANGE_OPTIONS.map(opt => {
            const active = opt.id === rangeId;
            return (
              <button key={opt.id} type="button" onClick={() => {
                if (opt.id === 'custom') { setShowCustom(true); return; }
                setShowCustom(false);
                onChange(opt.id, null);
                setOpen(false);
              }} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px',
                background: active ? T.brand.soft : 'transparent', border: 'none', borderRadius: 8,
                color: active ? T.brand.base : T.text.primary,
                fontSize: 13, fontWeight: active ? 700 : 500,
                cursor: 'pointer', outline: 'none', fontFamily: T.font.sans,
              }}>{opt.label}</button>
            );
          })}
          {showCustom && (
            <div style={{
              marginTop: 4, padding: 12,
              borderTop: '1px solid ' + T.border.subtle,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  From
                  <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} style={{
                    padding: '8px 10px', border: '1px solid ' + T.border.subtle, borderRadius: 8,
                    fontSize: 13, fontFamily: T.font.sans, color: T.text.primary,
                    backgroundColor: T.surface.card, outline: 'none',
                  }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  To
                  <input type="date" value={to} min={from || undefined} max={todayIso()} onChange={(e) => setTo(e.target.value)} style={{
                    padding: '8px 10px', border: '1px solid ' + T.border.subtle, borderRadius: 8,
                    fontSize: 13, fontFamily: T.font.sans, color: T.text.primary,
                    backgroundColor: T.surface.card, outline: 'none',
                  }} />
                </label>
                <button type="button" disabled={!from || !to} onClick={() => {
                  onChange('custom', { from, to });
                  setOpen(false);
                }} style={{
                  marginTop: 4, padding: '9px 12px',
                  background: (!from || !to) ? 'rgba(31,42,63,0.10)' : T.brand.base,
                  color: '#FFFFFF', fontSize: 13, fontWeight: 700,
                  border: 'none', borderRadius: 8,
                  cursor: (!from || !to) ? 'not-allowed' : 'pointer',
                  fontFamily: T.font.sans, outline: 'none',
                }}>Apply range</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExportPill({ onExport }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  const formats = [
    { id: 'XLSX', label: 'Excel spreadsheet',  ext: '.xlsx', accent: '#4CAF50' },
    { id: 'CSV',  label: 'Comma-separated',    ext: '.csv',  accent: '#7C4DFF' },
    { id: 'PDF',  label: 'PDF document',       ext: '.pdf',  accent: '#FF6B9D' },
  ];
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
        background: T.surface.card, border: '1px solid ' + (open ? T.brand.base : T.border.subtle),
        borderRadius: 12, color: T.text.primary, fontSize: 13, fontWeight: 600,
        cursor: 'pointer', outline: 'none', fontFamily: T.font.sans,
      }}>
        <span style={{ color: T.text.secondary, display: 'inline-flex' }}><DownloadIcon /></span>
        Export
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: 220,
          backgroundColor: T.surface.card, border: '1px solid ' + T.border.subtle,
          borderRadius: 12, boxShadow: '0 12px 32px rgba(31,42,63,0.12)',
          padding: 6, zIndex: 20,
        }}>
          {formats.map(f => (
            <button key={f.id} type="button" onClick={() => { setOpen(false); onExport && onExport(f.id); }} style={{
              display: 'grid', gridTemplateColumns: '32px 1fr auto',
              alignItems: 'center', columnGap: 10,
              width: '100%', textAlign: 'left', padding: '8px 10px',
              background: 'transparent', border: 'none', borderRadius: 8,
              color: T.text.primary, cursor: 'pointer', outline: 'none', fontFamily: T.font.sans,
            }}>
              <span style={{
                width: 32, height: 32, borderRadius: 8,
                backgroundColor: f.accent + '22',
                color: f.accent, fontSize: 10, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{f.id}</span>
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{f.id}</span>
                <span style={{ fontSize: 11, color: T.text.muted }}>{f.label}</span>
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, fontFamily: T.font.mono }}>{f.ext}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewReportButton({ onClick }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '9px 16px',
        background: pressed ? '#E36900' : (hover ? '#FF8C2A' : T.brand.base),
        border: '1px solid ' + (pressed ? '#E36900' : T.brand.base),
        borderRadius: 12,
        color: '#FFFFFF', fontSize: 13, fontWeight: 700,
        cursor: 'pointer', outline: 'none', flexShrink: 0,
        fontFamily: T.font.sans,
        transform: pressed ? 'translateY(1px) scale(0.98)' : (hover ? 'translateY(-1px)' : 'translateY(0)'),
        boxShadow: pressed
          ? 'inset 0 2px 4px rgba(0,0,0,0.18), 0 0 0 3px rgba(252,128,25,0.20)'
          : (hover
              ? '0 4px 12px rgba(252,128,25,0.40)'
              : '0 2px 6px rgba(252,128,25,0.30)'),
        transition: 'transform 80ms ease, box-shadow 120ms ease, background 120ms ease',
      }}
    >
      <PlusIcon />
      New Report
    </button>
  );
}

function NotificationsBell({ notifications, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  const unread = notifications.filter(n => !n.read).length;
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen(v => !v)}
        style={{
          position: 'relative',
          width: 38, height: 38, borderRadius: 12,
          backgroundColor: T.surface.card,
          border: '1px solid ' + (open ? T.brand.base : T.border.subtle),
          color: T.text.secondary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', outline: 'none',
        }}
      >
        <BellIcon />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -3, right: -3,
            minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 999, backgroundColor: '#FF4757',
            color: '#FFFFFF', fontSize: 9, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid ' + T.surface.canvas,
            boxSizing: 'border-box', lineHeight: 1,
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          width: 340,
          backgroundColor: T.surface.card,
          border: '1px solid ' + T.border.subtle,
          borderRadius: 12,
          boxShadow: '0 12px 32px rgba(31,42,63,0.12)',
          overflow: 'hidden', zIndex: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid ' + T.border.subtle }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Notifications</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.text.muted }}>{unread} unread</span>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: T.text.muted }}>You are all caught up.</div>
            ) : notifications.map(n => (
              <button
                key={n.id}
                type="button"
                onClick={() => { setOpen(false); onNavigate(n.target); }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '6px 1fr auto',
                  alignItems: 'flex-start',
                  columnGap: 10,
                  width: '100%', textAlign: 'left',
                  padding: '12px 14px',
                  background: 'transparent',
                  border: 'none', borderBottom: '1px solid ' + T.border.subtle,
                  cursor: 'pointer', outline: 'none', fontFamily: T.font.sans,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: n.read ? 'transparent' : T.brand.base, marginTop: 6 }} />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: T.text.primary, marginBottom: 2 }}>{n.title}</span>
                  <span style={{ fontSize: 12, color: T.text.muted, lineHeight: 1.4 }}>{n.body}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{n.when}</span>
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.brand.base, alignSelf: 'center', whiteSpace: 'nowrap' }}>View ›</span>
              </button>
            ))}
          </div>
          <div style={{ padding: '10px 14px', textAlign: 'center', borderTop: '1px solid ' + T.border.subtle }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.brand.base, cursor: 'pointer' }}>Mark all read</span>
          </div>
        </div>
      )}
    </div>
  );
}

const SEARCH_PHRASES = [
  'any metric',
  'a creator by name',
  'a fan by name',
  'a brand campaign',
  "today's revenue",
  'pending payouts',
  'refunds today',
  'a saved report',
];

function useTypewriter(phrases, paused) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [text, setText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [caret, setCaret] = useState(true);

  useEffect(() => {
    if (paused) return undefined;
    const phrase = phrases[phraseIdx % phrases.length];
    let delay;
    if (!deleting && text.length < phrase.length) delay = 65;
    else if (!deleting && text.length === phrase.length) delay = 1400;
    else if (deleting && text.length > 0) delay = 35;
    else delay = 280;

    const t = setTimeout(() => {
      if (!deleting && text.length < phrase.length) {
        setText(phrase.slice(0, text.length + 1));
      } else if (!deleting && text.length === phrase.length) {
        setDeleting(true);
      } else if (deleting && text.length > 0) {
        setText(phrase.slice(0, text.length - 1));
      } else {
        setDeleting(false);
        setPhraseIdx((i) => (i + 1) % phrases.length);
      }
    }, delay);
    return () => clearTimeout(t);
  }, [phrases, phraseIdx, text, deleting, paused]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setCaret((v) => !v), 500);
    return () => clearInterval(t);
  }, [paused]);

  return paused ? '' : ('Search ' + text + (caret ? '|' : ' '));
}

// Searchable corpus beyond just page-nav: metrics + people (mock).
// Each item knows what page to land on when clicked.
const METRICS = [
  { id: 'm-revenue-today',     label: 'Revenue today',           page: 'home',     hint: 'Hero metric' },
  { id: 'm-revenue-trend',     label: 'Revenue trend (12 mo)',   page: 'money',    hint: 'Money / trend' },
  { id: 'm-revenue-mix',       label: 'Revenue mix',             page: 'money',    hint: 'Money / mix' },
  { id: 'm-pending-payouts',   label: 'Pending payouts',         page: 'money',    hint: 'Money / cashflow' },
  { id: 'm-fans-online',       label: 'Fans online',             page: 'home',     hint: 'Live now' },
  { id: 'm-creators-online',   label: 'Creators online',         page: 'home',     hint: 'Live now' },
  { id: 'm-fans-by-source',    label: 'Fans by source',          page: 'fans',     hint: 'Fans deep-dive' },
  { id: 'm-engagement-tiers',  label: 'Fan engagement tiers',    page: 'fans',     hint: 'Fans deep-dive' },
  { id: 'm-top-creators',      label: 'Top creators',            page: 'creators', hint: 'Leaderboard' },
  { id: 'm-at-risk-creators',  label: 'At-risk creators',        page: 'creators', hint: 'Creators / risk' },
  { id: 'm-creator-funnel',    label: 'Creator funnel',          page: 'acquisition', hint: 'Acquisition' },
  { id: 'm-brand-funnel',      label: 'Brand funnel',            page: 'acquisition', hint: 'Acquisition' },
  { id: 'm-brand-campaigns',   label: 'Active brand campaigns',  page: 'brands',   hint: 'Brands' },
  { id: 'm-top-brands',        label: 'Top brands by spend',     page: 'brands',   hint: 'Leaderboard' },
  { id: 'm-calls-today',       label: 'Calls today',             page: 'operations', hint: 'Operations' },
  { id: 'm-call-outcomes',     label: 'Call outcomes',           page: 'operations', hint: 'Operations' },
  { id: 'm-refunds-today',     label: 'Refunds today',           page: 'operations', hint: 'Operations' },
  { id: 'm-gst-collected',     label: 'GST collected (MTD)',     page: 'audit',    hint: 'Audit & Tax' },
  { id: 'm-tds-withheld',      label: 'TDS withheld',            page: 'audit',    hint: 'Audit & Tax' },
];
// People search corpus removed pre-launch — the previous list was hardcoded
// fake names, which would have surfaced in CEO demos as "real" creators/fans/
// brands. The search now scopes to pages + metrics until a real /search endpoint
// is wired (returns deduped users by name + masked fan handles).
const PEOPLE = [];

function GroupHeader({ label }) {
  return (
    <div style={{ padding: '8px 12px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.text.muted }}>{label}</div>
  );
}
function SuggestionRow({ left, label, right, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 10,
      width: '100%', textAlign: 'left',
      padding: '9px 12px', background: 'transparent', border: 'none', borderRadius: 8,
      color: T.text.primary, fontSize: 13, fontWeight: 500,
      cursor: 'pointer', outline: 'none', fontFamily: T.font.sans,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: left || T.brand.base }} />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {right && <span style={{ fontSize: 10, fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{right}</span>}
    </button>
  );
}

function NavSearch({ pages, onSelect }) {
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setFocused(false));
  const term = q.trim().toLowerCase();
  const pagesMatched   = term ? pages.filter(p => p.title.toLowerCase().includes(term)).slice(0, 5) : [];
  const metricsMatched = term ? METRICS.filter(m => m.label.toLowerCase().includes(term)).slice(0, 5) : [];
  const peopleMatched  = term ? PEOPLE.filter(p => p.label.toLowerCase().includes(term)).slice(0, 5) : [];
  const totalMatches = pagesMatched.length + metricsMatched.length + peopleMatched.length;
  const typewriter = useTypewriter(SEARCH_PHRASES, focused || q.length > 0);

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 380 }}>
      <style>{'.fc-search-input::selection { background: rgba(252,128,25,0.30); } .fc-search-input:focus { outline: none; box-shadow: none; }'}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: T.surface.card, border: '1px solid ' + (focused ? T.brand.base : T.border.subtle), borderRadius: 12, transition: 'border-color 120ms ease' }}>
        <span style={{ color: T.text.muted, display: 'inline-flex' }}><SearchIcon /></span>
        <input
          type="text"
          className="fc-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setQ(''); setFocused(false); e.target.blur(); } }}
          placeholder={typewriter || 'Search any metric, creator, fan...'}
          style={{
            flex: 1, border: 'none', outline: 'none', boxShadow: 'none',
            background: 'transparent', color: T.text.primary,
            fontFamily: T.font.sans, fontSize: 13,
            WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
        />
      </div>
      {focused && q && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          backgroundColor: T.surface.card, border: '1px solid ' + T.border.subtle,
          borderRadius: 12, boxShadow: '0 12px 32px rgba(31,42,63,0.12)',
          padding: 6, zIndex: 20, maxHeight: 380, overflowY: 'auto',
        }}>
          {totalMatches === 0 && (
            <div style={{ padding: '14px 12px', color: T.text.muted, fontSize: 13, textAlign: 'center' }}>
              No matches for "<strong>{q}</strong>".
            </div>
          )}
          {pagesMatched.length > 0 && (
            <>
              <GroupHeader label="Pages" />
              {pagesMatched.map(m => (
                <SuggestionRow key={m.id} left={T.brand.base} label={m.title} right="page"
                  onClick={() => { onSelect(m.id); setQ(''); setFocused(false); }} />
              ))}
            </>
          )}
          {metricsMatched.length > 0 && (
            <>
              <GroupHeader label="Metrics" />
              {metricsMatched.map(m => (
                <SuggestionRow key={m.id} left="#7C4DFF" label={m.label} right={m.hint}
                  onClick={() => { onSelect(m.page); setQ(''); setFocused(false); }} />
              ))}
            </>
          )}
          {peopleMatched.length > 0 && (
            <>
              <GroupHeader label="People & brands" />
              {peopleMatched.map(p => (
                <SuggestionRow
                  key={p.id}
                  left={p.type === 'creator' ? '#4CAF50' : p.type === 'fan' ? '#FC8019' : '#FF6B9D'}
                  label={p.label}
                  right={p.type}
                  onClick={() => { onSelect(p.page); setQ(''); setFocused(false); }} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * AccountMenu — the user pill + dropdown with Sign out. Self-contained (owns its
 * open state, outside-click and Escape handling) so BOTH the desktop and the
 * mobile headers can render it. The mobile header previously showed only a
 * non-interactive avatar, so there was no way to sign out on a phone.
 *
 * `compact` (mobile) renders just the avatar; desktop also shows name + role.
 */
function AccountMenu({ user, onLogout, compact = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const handleSignOut = () => {
    setOpen(false);
    if (typeof onLogout === 'function') { onLogout(); return; }
    // Fallback: clear local auth and reload so the app re-renders LoginScreen
    // even if the parent forgot to wire onLogout.
    try {
      localStorage.removeItem('fc_token');
      localStorage.removeItem('fc_user');
      sessionStorage.removeItem('fc_welcome_shown');
    } catch (_) { /* private browsing — fall through */ }
    window.location.replace('/');
  };

  const avatar = (
    <span style={{
      width: 32, height: 32, borderRadius: '50%',
      background: 'linear-gradient(135deg, ' + T.brand.base + ' 0%, ' + T.brand.deep + ' 100%)',
      color: '#FFFFFF', fontSize: 12, fontWeight: 800,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>{user.initials}</span>
  );

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label="Open account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        style={compact ? {
          width: 32, height: 32, padding: 0, border: 'none', background: 'transparent',
          cursor: 'pointer', borderRadius: '50%', display: 'flex', flexShrink: 0, outline: 'none',
        } : {
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '5px 12px 5px 5px',
          background: T.surface.card, border: '1px solid ' + T.border.subtle,
          borderRadius: 999, cursor: 'pointer', font: 'inherit', color: 'inherit', outline: 'none',
        }}
      >
        {avatar}
        {!compact && (
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.text.primary }}>{user.name}</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{user.role}</span>
          </span>
        )}
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          minWidth: 200, background: T.surface.card,
          border: '1px solid ' + T.border.subtle, borderRadius: 12,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.10)',
          padding: 6, zIndex: 50,
        }}>
          <div style={{ padding: '8px 12px 6px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text.primary }}>{user.name}</div>
            <div style={{ fontSize: 10, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{user.role}</div>
          </div>
          <div style={{ height: 1, background: T.border.subtle, margin: '4px 0' }} />
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 12px', background: 'transparent', border: 0,
              borderRadius: 8, fontSize: 13, fontWeight: 600,
              color: T.text.primary, cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = T.surface.canvas; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function TopBar({
  pageTitle = 'Home',
  rangeId = 'today',
  customRange = null,
  onRangeChange = () => {},
  resolvedRange = null,   // { from, to }   — current window after resolveRange()
  prevRange     = null,   // { from, to, label } — prev window after resolvePrevRange()
  isMobile = false,
  onMenuClick = () => {},
  pages = [],
  onNavigate = () => {},
  liveStats = '',
  syncedAt,
  user = { initials: 'JT', name: 'Jasmin T', role: 'CEO' },
  notifications = [],
  onExport = () => {},
  onLogout = null,
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(id);
  }, []);
  const ago = syncedAt ? timeAgo(syncedAt) : 'just now';

  if (isMobile) {
    return (
      <header style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 14px', height: 65, boxSizing: 'border-box',
        borderBottom: '1px solid ' + T.border.subtle,
        backgroundColor: T.surface.canvas,
        fontFamily: T.font.sans,
      }}>
        <button
          type="button"
          aria-label="Open menu"
          onClick={onMenuClick}
          style={{
            width: 40, height: 40, borderRadius: 10,
            backgroundColor: T.surface.card,
            border: '1px solid ' + T.border.subtle,
            color: T.text.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', outline: 'none', flexShrink: 0,
          }}
        >
          <MenuIcon />
        </button>
        <h1 style={{ flex: 1, margin: 0, fontSize: 18, fontWeight: 700, color: T.text.primary, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {pageTitle}
        </h1>
        <AccountMenu user={user} onLogout={onLogout} compact />
      </header>
    );
  }

  return (
    <header style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 28px', height: 65, boxSizing: 'border-box',
      borderBottom: '1px solid ' + T.border.subtle,
      backgroundColor: T.surface.canvas,
      fontFamily: T.font.sans,
    }}>
      <button
        type="button"
        aria-label="Toggle sidebar"
        onClick={onMenuClick}
        style={{
          width: 38, height: 38, borderRadius: 10,
          backgroundColor: T.surface.card,
          border: '1px solid ' + T.border.subtle,
          color: T.text.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', outline: 'none', flexShrink: 0,
          marginRight: 4,
        }}
      >
        <MenuIcon />
      </button>
      <h1 style={{
        margin: 0, fontSize: 22, fontWeight: 700,
        color: T.text.primary, letterSpacing: '-0.02em',
        flex: '0 0 auto', minWidth: 0,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {pageTitle}
      </h1>

      {/* NavSearch hidden pre-launch (CEO directive 2026-05-06).
          The placeholder ("Search today's revenue") implied a live data
          search but the dropdown only navigated to page sections, with
          the People & brands corpus empty. Misleading for the CEO.
          Use the AI assistant (orange FAB) for live data questions, and
          the sidebar for navigation. The component is left in this file
          so it can be brought back once /search-accounts is wired into
          the topbar (post-launch).
      <NavSearch pages={pages} onSelect={onNavigate} />
      */}
      <div style={{ flex: 1 }} />{/* keeps the topbar layout balanced */}

      <LiveTicker fallbackAgo={ago} />
      {liveStats && (
        <span style={{ fontSize: 10, fontWeight: 600, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>
          {liveStats}
        </span>
      )}

      <div style={{ flex: 1 }} />

      {/* "+ New Report" hidden pre-launch — the ReportBuilder it opened still
          renders mock leaderboard previews and a mock "Schedule monthly delivery"
          alert. Use Relational reports for now. Re-enable when ReportBuilder is
          wired to live data. */}

      <DateRangePill rangeId={rangeId} customRange={customRange} onChange={onRangeChange} resolvedRange={resolvedRange} prevRange={prevRange} />

      {/* Notifications bell hidden pre-launch — earlier seed list was hardcoded
          fake alerts (refund spike, payouts, etc.) which would render as real
          on day 1. Re-enable once /api/v1/notifications is wired. */}

      <AccountMenu user={user} onLogout={onLogout} />
    </header>
  );
}
